// ============================================================================
// WailsBridge — автоопределение Wails desktop mode
// ============================================================================

import { createLogger } from '@/lib/logger';
import type { AppNotification } from '@/lib/notifications/notificationModel';
import { notificationThemeFromDocument } from '@/lib/notifications/notificationTheme';

const wailsLog = createLogger('WailsBridge');

declare global {
  interface Window {
    go?: {
      main?: {
        App?: {
          GetConfig: () => Promise<{
            center_url: string;
            db_path: string;
            /** Внешний публичный URL платформы (например "https://cityecon.thydra.ru") —
             * для QR-ссылок и абсолютных иконок. В отличие от center_url
             * (локальный адрес встроенного сервера 127.0.0.1:18080). */
            external_base_url?: string;
            /** loopback_url — локальный прокси стримов ЭТОГО экземпляра desktop
             * (напр. "http://127.0.0.1:18085"). Порт свой на инстанс (мульти-
             * инстанс), поэтому SDK адресуется на прокси СВОЕГО экземпляра, а
             * не на хардкод-константу. */
            loopback_url?: string;
          }>;
          OpenBase: (dbPath: string) => Promise<{
            center_url: string;
            db_path: string;
          }>;
          ChooseFolder: () => Promise<string>;
          /** Общий файл данных лаунчера (ВСЕ окна desktop): JSON-строка.
           *  '' = файла ещё нет (первый запуск → клиент переносит localStorage).
           *  См. zdc-client/src/lib/launcherStorage.ts (SSoT ключей). */
          LoadLauncherData: () => Promise<string>;
          /** Запись общего файла данных лаунчера (атомарно: temp+rename). */
          SaveLauncherData: (data: string) => Promise<void>;
          HideToTray: () => Promise<void>;
          ShowFromTray: () => Promise<void>;
          /** Переключить адрес центра для API-прокси AssetServer:
           *  '' = локальный встроенный сервер; иначе URL внешнего центра. */
          SetProxyTarget: (url: string) => Promise<void>;
          /** Пункт меню трея «Открыть <имя базы>»: имя активной базы (пусто =
           *  значение по умолчанию «Открыть HydraDatacentre», лаунчер). */
          SetTrayBaseName: (name: string) => Promise<void>;
          /** Удалённый доступ (host): применить пакет событий ввода (мышь/клавиатура).
           *  Контракт событий: plans/remote-protocol-contract.md §4.1. */
          SendRemoteInput: (eventsJSON: string) => Promise<{ ok: boolean; applied?: number; error?: string }>;
          /** Файлы устройства (host, Ф3): корни/каталоги/чтение/запись/удаление. */
          HostFsRoots: () => Promise<{ ok: boolean; roots?: string[]; error?: string }>;
          HostFsList: (path: string) => Promise<{ ok: boolean; path?: string; parent?: string; entries?: { name: string; is_dir: boolean; size: number; modified: string }[]; error?: string }>;
          HostFsRead: (path: string) => Promise<{ ok: boolean; name?: string; size?: number; data?: string; error?: string }>;
          HostFsWrite: (path: string, base64Data: string) => Promise<{ ok: boolean; size?: number; error?: string }>;
          HostFsDelete: (path: string) => Promise<{ ok: boolean; error?: string }>;
          /** Движок «Удалённого доступа» (M2, supervisor): probe/version/start/stop/status. */
          RemoteEngineProbe: () => Promise<{ available: boolean; path?: string; probe?: unknown; error?: string }>;
          RemoteEngineVersion: () => Promise<{ ok: boolean; version?: string; error?: string }>;
          RemoteEngineStart: (port: number) => Promise<{ ok: boolean; pid?: number; error?: string }>;
          RemoteEngineStop: () => Promise<{ ok: boolean; error?: string }>;
          RemoteEngineStatus: () => Promise<{ running: boolean; pid?: number; restarts?: number; uptime_sec?: number; path?: string }>;
          /** Окно уведомлений (desktop): карточка в ОТДЕЛЬНОМ окне поверх всех
           *  окон (как Telegram). payloadJSON — ЕДИНЫЙ контракт карточки
           *  (AppNotification + theme, см. lib/notifications/notificationModel).
           *  Возвращает true, если уведомление принято окном (попап в главном
           *  окне НЕ нужен); false — окно недоступно. */
          ShowNotify: (payloadJSON: string) => Promise<boolean>;
        };
      };
    };
  }
}

export interface WailsConfig {
  centerUrl: string;
  externalBaseUrl: string;
  /** Фактический loopback-прокси ЭТОГО экземпляра desktop (из GetConfig). */
  loopbackUrl: string;
  isWails: boolean;
}

let wailsConfig: WailsConfig | null = null;
// null = loopback-адрес ЭТОГО экземпляра ещё не известен: порт выдаёт ОС при
// старте процесса (мульти-инстанс), поэтому «стартовой константы» не существует —
// SDK на desktop создаётся ТОЛЬКО после чтения конфига Go (main.tsx ждёт его до
// первого рендера; getLoopbackUrl() до этого бросает явную ошибку).
let loopbackUrl: string | null = null;

const configListeners = new Set<(config: WailsConfig) => void>();

// Слушатели смены конфига (по факту — смены loopback-адреса инстанса):
// sdkStore пересоздаёт SDK, чтобы он адресовался на прокси своего экземпляра.
export function onWailsConfigChange(listener: (config: WailsConfig) => void): () => void {
  configListeners.add(listener);
  return () => {
    configListeners.delete(listener);
  };
}

function applyConfig(config: WailsConfig): WailsConfig {
  const prevLoopback = loopbackUrl;
  loopbackUrl = config.loopbackUrl || null;
  // Уведомляем и на ПЕРВУЮ установку адреса: SDK мог быть создан в узком окне
  // старта до чтения конфига — его нужно пересоздать (порт заранее неизвестен).
  if (prevLoopback !== loopbackUrl) {
    for (const listener of configListeners) {
      try {
        listener(config);
      } catch (e) {
        wailsLog.warn('слушатель смены конфига выбросил ошибку', { error: e instanceof Error ? e.message : String(e) });
      }
    }
  }
  return config;
}

/**
 * Прочитать/перечитать конфиг desktop напрямую из Go (App.GetConfig).
 * Вызывается при старте и при переключении центра/инстанса, чтобы SDK
 * получил АКТУАЛЬНЫЙ loopback_url, а не хардкод-порт.
 */
export async function refreshWailsConfig(): Promise<WailsConfig> {
  if (window.go?.main?.App?.GetConfig) {
    try {
      const config = await window.go.main.App.GetConfig();
      const loopback = String(config.loopback_url || '').trim().replace(/\/+$/, '');
      if (!loopback) {
        // Desktop-конфиг без loopback_url: адрес прокси НЕ выдумываем —
        // SDK до его появления не создаётся (getLoopbackUrl бросает).
        wailsLog.error('GetConfig без loopback_url — SDK desktop не будет создан до фактического адреса', {
          centerUrl: config.center_url || '',
        });
      }
      wailsConfig = applyConfig({
        centerUrl: config.center_url || '',
        externalBaseUrl: config.external_base_url || '',
        loopbackUrl: loopback,
        isWails: true,
      });
      wailsLog.info('конфиг desktop получен', {
        centerUrl: wailsConfig.centerUrl,
        loopbackUrl: wailsConfig.loopbackUrl,
      });
      return wailsConfig;
    } catch (e) {
      wailsLog.warn('GetConfig недоступен', { error: e instanceof Error ? e.message : String(e) });
    }
  }

  wailsConfig = applyConfig({
    centerUrl: '',
    externalBaseUrl: '',
    loopbackUrl: '',
    isWails: false,
  });
  return wailsConfig;
}

export async function detectWails(): Promise<WailsConfig> {
  if (wailsConfig) return wailsConfig;
  return refreshWailsConfig();
}

/**
 * Синхронный доступ к loopback-адресу прокси ЭТОГО экземпляра (для getSDK).
 * Источник — конфиг Go (App.GetConfig().loopback_url; порт выдаёт ОС).
 * До чтения конфига адреса НЕТ (бросаем явную ошибку, а не мёртвый порт):
 * main.tsx читает конфиг ДО первого рендера, поэтому этот путь — программная
 * ошибка, а не пользовательский сценарий.
 */
export function getLoopbackUrl(): string {
  if (loopbackUrl !== null) return loopbackUrl;
  throw new Error('desktop-конфиг ещё не прочитан: loopback_url неизвестен');
}

export function isWailsMode(): boolean {
  // Детерминированно и СИНХРОННО: если есть Wails bridge — это desktop.
  // Проверяем window.go НАПРЯМУЮ, а не только кэш wailsConfig —
  // bridge может появиться ПОСЛЕ первого вызова detectWails().
  if (typeof window !== 'undefined' && !!window.go?.main?.App) return true;
  if (wailsConfig) return wailsConfig.isWails;
  return false;
}

// ============================================================================
// setProxyTarget — переключает адрес центра для API-прокси десктопа
// ('' = локальный встроенный сервер; иначе URL внешнего центра).
// Используется sdkStore при смене centerUrl на desktop: WebView2 всегда
// ходит на same-origin (wails.localhost), Go проксирует на реальный центр —
// нет mixed content/CORS/Private Network Access.
// ============================================================================
export function setProxyTarget(url: string): void {
  try {
    if (typeof window !== 'undefined' && window.go?.main?.App?.SetProxyTarget) {
      window.go.main.App.SetProxyTarget(url || '');
    }
  } catch {
    // bridge недоступен (web) — не критично
  }
}

// ============================================================================
// setTrayBaseName — подпись пункта меню трея desktop «Открыть <имя базы>».
// Вызывается при выборе/входе в базу (отображаемое имя базы); '' — сброс к
// значению по умолчанию (лаунчер). В web/PWA моста нет — вызов no-op.
// ============================================================================
export function setTrayBaseName(name: string): void {
  try {
    if (typeof window !== 'undefined' && window.go?.main?.App?.SetTrayBaseName) {
      void window.go.main.App.SetTrayBaseName(name || '');
    }
  } catch {
    // bridge недоступен (web) — не критично
  }
}

// ============================================================================
// Общий файл данных лаунчера (desktop, ВСЕ окна) — мост App.LoadLauncherData/
// SaveLauncherData. Используется ТОЛЬКО lib/launcherStorage.ts (единый слой);
// web/PWA не вызывает (localStorage). Отсутствие моста — явная ошибка
// (вызов без desktop-бриджа недопустим — фоллбэков нет).
// ============================================================================
export async function loadLauncherFile(): Promise<string> {
  const api = typeof window !== 'undefined' ? window.go?.main?.App : undefined;
  if (!api?.LoadLauncherData) {
    throw new Error('Wails bridge: LoadLauncherData недоступен');
  }
  return await api.LoadLauncherData();
}

export async function saveLauncherFile(data: string): Promise<void> {
  const api = typeof window !== 'undefined' ? window.go?.main?.App : undefined;
  if (!api?.SaveLauncherData) {
    throw new Error('Wails bridge: SaveLauncherData недоступен');
  }
  await api.SaveLauncherData(data);
}

// ============================================================================
// getExternalBaseUrl — ВНЕШНИЙ публичный URL платформы для QR-ссылок/иконок.
// В WebView2 window.location.origin = "http://wails.localhost" — телефон не
// может открыть такой URL. Поэтому внешний URL берём ИЗ КОНФИГА GO
// (App.GetConfig().external_base_url, задаётся HYDRA_EXTERNAL_BASE_URL или
// config.json в data dir), а НЕ из window.location.origin.
// Не Wails / пустой — возвращаем window.location.origin (браузер/PWA).
// ============================================================================
let cachedExternalBaseUrl: string | null = null;

export async function getExternalBaseUrl(): Promise<string> {
  if (cachedExternalBaseUrl) return cachedExternalBaseUrl;
  const { externalBaseUrl, isWails } = await detectWails();
  const base = isWails && externalBaseUrl ? externalBaseUrl : window.location.origin;
  cachedExternalBaseUrl = String(base || '').replace(/\/+$/, '');
  return cachedExternalBaseUrl;
}

// ============================================================================
// ОКНО УВЕДОМЛЕНИЙ (desktop): отдельное окно поверх всех окон (как Telegram).
// showDesktopNotify возвращает true, если карточку приняло ОКНО (клиенту не
// нужно дублировать попап в главном окне); false — окна нет (web/PWA, окно не
// поднялось) → вызывающий показывает in-app стек.
// Карточка передаётся ОДНИМ JSON-контрактом (AppNotification + theme): окно
// не собирает поля из отдельных аргументов и не теряет текст/контекст.
// ============================================================================
export async function showDesktopNotify(notification: AppNotification): Promise<boolean> {
  const api = typeof window !== 'undefined' ? window.go?.main?.App : undefined;
  if (!api?.ShowNotify) return false;
  try {
    const payload = JSON.stringify({
      ...notification,
      theme: notification.theme || notificationThemeFromDocument(),
    });
    const shown = await api.ShowNotify(payload);
    return shown === true;
  } catch (e) {
    wailsLog.warn('ShowNotify не сработал — уведомление в главном окне', {
      error: e instanceof Error ? e.message : String(e),
    });
    return false;
  }
}
